import { CheckCircle2, CircleDashed } from "lucide-react";
import {
  BELOW_BUG_BAR,
  fogbugzBugBarResultSchema,
  type FogbugzBugBarResult,
} from "@ttc-spectree/toolbox-contract/fogbugz-formulator";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/utils";

interface BugBarPanelProps {
  /** The Bug bar result the server computed on the latest saved report. */
  bugBar: FogbugzBugBarResult;
  /**
   * Shown under the heading when the bar fails, e.g. how to re-check. Omit for
   * the plain list.
   */
  hint?: string | undefined;
  className?: string | undefined;
}

/**
 * True when a Bug bar result keeps a case from being filed: the bar applies (a
 * bug case) and does not pass. `undefined` (no result yet) never blocks — the
 * server is the gate and refuses below-bar submits itself.
 */
export function bugBarBlocks(bugBar: FogbugzBugBarResult | null | undefined): boolean {
  return bugBar !== null && bugBar !== undefined && bugBar.applies && !bugBar.passes;
}

/**
 * The Bug bar result carried by a 400 `BELOW_BUG_BAR` submit refusal, or null
 * for any other error. The details are parsed with the contract schema rather
 * than trusted, so a malformed body falls back to the plain error message.
 * Shared by the website FogBugz Formulator and the Toolbox one.
 */
export function belowBugBarFrom(error: unknown): FogbugzBugBarResult | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.data as { error?: { code?: unknown; details?: unknown } } | undefined;
  if (body?.error?.code !== BELOW_BUG_BAR) return null;
  const parsed = fogbugzBugBarResultSchema.safeParse(body.error.details);
  return parsed.success ? parsed.data : null;
}

/**
 * The FogBugz Bug bar result in plain words (epic PI-SPEC-SW-E309, ER-1665
 * Resolved Decisions 4 and 4b): "Ready to file" when a bug case meets the bar,
 * otherwise "Still needed before filing" with one line per missing rule (its
 * label and what to write). Renders nothing for a feature or inquiry, where the
 * bar does not apply.
 *
 * Shared by the website FogBugz Formulator's review and submit steps and the
 * Toolbox FogBugz Formulator page, so both show the same list.
 */
export function BugBarPanel({ bugBar, hint, className }: BugBarPanelProps) {
  if (!bugBar.applies) return null;

  if (bugBar.passes) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-sm text-green-500",
          className
        )}
        data-testid="bug-bar-panel"
        data-state="passes"
      >
        <CheckCircle2 className="h-4 w-4" />
        <span className="font-medium">Ready to file</span>
      </div>
    );
  }

  return (
    <div
      className={cn("p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm space-y-2", className)}
      data-testid="bug-bar-panel"
      data-state="below"
      role="status"
    >
      <div className="flex items-center gap-2 font-medium text-yellow-500">
        <CircleDashed className="h-4 w-4" />
        <span>Still needed before filing</span>
      </div>
      {hint && <p className="text-muted-foreground">{hint}</p>}
      <ul className="space-y-1.5" data-testid="bug-bar-missing">
        {bugBar.missing.map((miss) => (
          <li key={`${miss.source}-${miss.ruleId}`} className="text-xs">
            <span className="font-medium">{miss.label}</span>
            <span className="block text-muted-foreground">{miss.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
